import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthFetch } from "../lib/authFetch";
import { useCorpora, corpusLabel } from "../lib/useCorpora";

// Skills page (#106): make the skill lifecycle visible and operable.
// A skill is the extraction contract behind a document type. Lifecycle:
//   induce (draft from exemplars) -> review -> approve (activates + links a
//   corpus type + bumps version) -> re-index to re-extract.

const INDEXING_API = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:3001";
const CORPUS_API = process.env.REACT_APP_CORPUS_API_URL ?? "http://localhost:3002";

interface ExtractionField {
  name: string;
  type: string;
  required?: boolean;
  description?: string;
}

interface SkillRow {
  skill_id: string;
  name: string;
  definition: string;
  has_prompt_body: boolean;
  summary_model: string | null;
  version: number;
  extraction_schema: ExtractionField[] | null;
  summary_guidance: string | null;
  status: string;
  origin: string;
  owner_corpus_id: string | null;
  linked_types: Array<{ corpus_id: string; type_name: string }>;
}

interface CorpusType {
  type_name: string;
  definition: string;
  skill_id: string | null;
  origin: string;
  document_count: number;
}

const SkillsPage: React.FC = () => {
  const authFetch = useAuthFetch();
  const { corpora } = useCorpora();

  const [skills, setSkills] = useState<SkillRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Induce form
  const writableCorpora = useMemo(() => corpora.filter((c) => c.owned || c.permissions.write), [corpora]);
  const [induceCorpus, setInduceCorpus] = useState("");
  const [induceType, setInduceType] = useState("");
  const [corpusTypes, setCorpusTypes] = useState<CorpusType[]>([]);
  const [inducing, setInducing] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  // Approve form state per draft skill
  const [approveTarget, setApproveTarget] = useState<Record<string, { corpusId: string; typeName: string }>>({});
  const [approving, setApproving] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${INDEXING_API}/query/skills`);
      const body = await res.json();
      if (!res.ok) throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
      setSkills((body as { skills: SkillRow[] }).skills);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load skills");
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { refresh(); }, [refresh]);

  // Load the type vocabulary when an induce corpus is picked.
  useEffect(() => {
    if (!induceCorpus) { setCorpusTypes([]); return; }
    let cancelled = false;
    authFetch(`${INDEXING_API}/query/corpus-types?corpusId=${encodeURIComponent(induceCorpus)}`)
      .then((res) => res.json())
      .then((body) => { if (!cancelled) setCorpusTypes((body as { types?: CorpusType[] }).types ?? []); })
      .catch(() => { if (!cancelled) setCorpusTypes([]); });
    return () => { cancelled = true; };
  }, [induceCorpus, authFetch]);

  const handleInduce = async () => {
    setInducing(true);
    setFlash(null);
    try {
      const res = await authFetch(`${INDEXING_API}/query/skills/induce`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ corpusId: induceCorpus, typeName: induceType }),
      });
      const body = (await res.json()) as { error?: string; skillId?: string; extractionSchema?: unknown[] };
      if (!res.ok) { setFlash(`Induction failed: ${body.error ?? `HTTP ${res.status}`}`); return; }
      setFlash(`Draft '${body.skillId}' created with ${(body.extractionSchema ?? []).length} fields — review and approve it below.`);
      refresh();
    } catch (err) {
      setFlash(`Induction failed: ${err instanceof Error ? err.message : "network error"}`);
    } finally {
      setInducing(false);
    }
  };

  const handleApprove = async (skill: SkillRow) => {
    const target = approveTarget[skill.skill_id];
    if (!target?.corpusId || !target?.typeName) return;
    setApproving(skill.skill_id);
    setFlash(null);
    try {
      const res = await authFetch(`${INDEXING_API}/query/skills/${encodeURIComponent(skill.skill_id)}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ corpusId: target.corpusId, typeName: target.typeName }),
      });
      const body = (await res.json()) as { error?: string };
      if (!res.ok) { setFlash(`Approval failed: ${body.error ?? `HTTP ${res.status}`}`); return; }
      setFlash(`'${skill.name}' is now active for ${target.typeName}.`);
      setJustApproved({ corpusId: target.corpusId, typeName: target.typeName });
      refresh();
    } catch (err) {
      setFlash(`Approval failed: ${err instanceof Error ? err.message : "network error"}`);
    } finally {
      setApproving(null);
    }
  };

  const [justApproved, setJustApproved] = useState<{ corpusId: string; typeName: string } | null>(null);
  const [typeReindexing, setTypeReindexing] = useState(false);

  // One-click scoped re-index after approval (#108): re-extract only the
  // approved type's documents.
  const handleTypeReindex = async () => {
    if (!justApproved) return;
    setTypeReindexing(true);
    try {
      const res = await authFetch(`${CORPUS_API}/api/corpora/${encodeURIComponent(justApproved.corpusId)}/reindex`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ documentType: justApproved.typeName }),
      });
      const body = (await res.json()) as { error?: string; queued?: number };
      if (!res.ok) { setFlash(`Re-index failed: ${body.error ?? `HTTP ${res.status}`}`); return; }
      setFlash(`Queued ${body.queued ?? 0} '${justApproved.typeName}' document(s) for re-extraction.`);
      setJustApproved(null);
    } catch (err) {
      setFlash(`Re-index failed: ${err instanceof Error ? err.message : "network error"}`);
    } finally {
      setTypeReindexing(false);
    }
  };

  const [copyTarget, setCopyTarget] = useState<Record<string, string>>({});
  const [copying, setCopying] = useState<string | null>(null);

  const handleCopy = async (skill: SkillRow) => {
    const toCorpusId = copyTarget[skill.skill_id];
    if (!toCorpusId) return;
    setCopying(skill.skill_id);
    setFlash(null);
    try {
      const res = await authFetch(`${INDEXING_API}/query/skills/${encodeURIComponent(skill.skill_id)}/copy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ toCorpusId }),
      });
      const body = (await res.json()) as { error?: string; skillId?: string };
      if (!res.ok) { setFlash(`Copy failed: ${body.error ?? `HTTP ${res.status}`}`); return; }
      setFlash(`Copied to '${body.skillId}' — the copy is independent; approve it against a type in the target corpus.`);
      refresh();
    } catch (err) {
      setFlash(`Copy failed: ${err instanceof Error ? err.message : "network error"}`);
    } finally {
      setCopying(null);
    }
  };

  const corpusName = (id: string | null) => {
    if (!id) return "(unowned)";
    const c = corpora.find((x) => x.corpusId === id);
    return c ? corpusLabel(c) : id;
  };

  const toggle = (id: string) =>
    setExpanded((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });

  const statusBadge = (status: string) =>
    status === "active"
      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
      : "bg-amber-50 text-amber-700 border border-amber-200";

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center gap-3 px-6 h-[78px] border-b border-krule bg-white shrink-0">
        <div>
          <h2 className="text-xl font-light tracking-tight text-ink">Skills</h2>
          <p className="text-xs font-mono text-kslate mt-0.5">Extraction contracts behind document types</p>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
        <div className="max-w-4xl space-y-6">
          {/* Lifecycle explainer */}
          <div className="bg-white border border-krule rounded-xl p-5 text-sm text-kslate leading-relaxed">
            A <span className="font-medium text-ink">skill</span> defines what gets extracted from a document type:
            named, typed fields (some required) plus summary guidance. Lifecycle:{" "}
            <span className="font-mono text-xs">induce</span> reads a type's existing documents and drafts a schema →
            you review the draft below → <span className="font-mono text-xs">approve</span> activates it and links the
            corpus type → a corpus <span className="font-medium text-ink">re-index</span> re-extracts that type's
            documents (only they regenerate — the skill's version stamp invalidates exactly their caches).
          </div>

          {/* Induce form */}
          <div className="bg-white border border-krule rounded-xl p-5">
            <h3 className="text-base font-medium text-ink mb-3">Induce a skill draft</h3>
            <div className="flex flex-wrap gap-3 items-center">
              <select
                value={induceCorpus}
                onChange={(e) => { setInduceCorpus(e.target.value); setInduceType(""); }}
                className="text-sm border border-krule rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-kamber/70"
              >
                <option value="">Corpus…</option>
                {writableCorpora.map((c) => (
                  <option key={c.corpusId} value={c.corpusId}>{corpusLabel(c)}</option>
                ))}
              </select>
              <select
                value={induceType}
                onChange={(e) => setInduceType(e.target.value)}
                disabled={!induceCorpus}
                className="text-sm border border-krule rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-kamber/70 disabled:opacity-50"
              >
                <option value="">Document type…</option>
                {corpusTypes
                  .filter((t) => t.document_count >= 3)
                  .map((t) => (
                    <option key={t.type_name} value={t.type_name}>
                      {t.type_name} ({t.document_count} docs{t.skill_id ? " — has skill" : ""})
                    </option>
                  ))}
              </select>
              <button
                onClick={handleInduce}
                disabled={inducing || !induceCorpus || !induceType}
                className="bg-ink hover:bg-inklight disabled:bg-ink/30 text-white text-sm font-medium rounded-lg px-5 py-2 transition-colors"
              >
                {inducing ? "Reading exemplars…" : "Induce draft"}
              </button>
            </div>
            <p className="text-xs text-kfaint mt-2">
              Types need at least 3 summarised documents. Re-inducing overwrites an existing draft, never an active skill.
            </p>
          </div>

          {flash && (
            <div className={`rounded-lg border px-4 py-3 text-sm flex items-center gap-3 flex-wrap ${flash.includes("failed") ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
              <span className="flex-1">{flash}</span>
              {justApproved && (
                <button
                  onClick={handleTypeReindex}
                  disabled={typeReindexing}
                  className="text-xs font-medium rounded-lg px-4 py-1.5 bg-ink hover:bg-inklight text-white transition-colors disabled:opacity-50 shrink-0"
                >
                  {typeReindexing ? "Queuing…" : `Re-index "${justApproved.typeName}" now`}
                </button>
              )}
            </div>
          )}

          {/* Skills list */}
          {loading && <p className="text-sm text-kslate">Loading skills…</p>}
          {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

          {!loading && !error && (
            <div className="space-y-3">
              {skills.map((s) => (
                <div key={s.skill_id} className="bg-white border border-krule rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggle(s.skill_id)}
                    className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-paper/40 transition-colors"
                  >
                    <span className={`shrink-0 px-2 py-0.5 rounded text-[11px] font-medium ${statusBadge(s.status)}`}>
                      {s.status}
                    </span>
                    <span className="text-sm font-medium text-ink">{s.name}</span>
                    <span className="text-[11px] font-mono text-kfaint">v{s.version} · {s.origin}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-kamber/10 text-ink border border-kamber/30 shrink-0">
                      {corpusName(s.owner_corpus_id)}
                    </span>
                    <span className="text-xs text-kslate truncate flex-1">
                      {(s.extraction_schema ?? []).length > 0
                        ? `${(s.extraction_schema ?? []).length} extraction fields`
                        : s.has_prompt_body ? "legacy authored prompt" : "no schema"}
                    </span>
                    <span className="flex flex-wrap gap-1 justify-end">
                      {s.linked_types.map((t) => (
                        <span key={`${t.corpus_id}:${t.type_name}`} className="text-[10px] px-1.5 py-0.5 rounded bg-paper text-kslate border border-krule">
                          {t.type_name}
                        </span>
                      ))}
                    </span>
                  </button>

                  {expanded.has(s.skill_id) && (
                    <div className="px-5 pb-4 pt-1 border-t border-krule/60 space-y-3">
                      <p className="text-sm text-kslate">{s.definition}</p>

                      {(s.extraction_schema ?? []).length > 0 && (
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-kfaint uppercase tracking-wide">
                              <th className="py-1 pr-3">Field</th>
                              <th className="py-1 pr-3">Type</th>
                              <th className="py-1 pr-3">Required</th>
                              <th className="py-1">Description</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-krule/40">
                            {(s.extraction_schema ?? []).map((f) => (
                              <tr key={f.name}>
                                <td className="py-1.5 pr-3 font-mono text-ink">{f.name}</td>
                                <td className="py-1.5 pr-3 font-mono text-kslate">{f.type}</td>
                                <td className="py-1.5 pr-3">{f.required ? "✓" : "—"}</td>
                                <td className="py-1.5 text-kslate">{f.description}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}

                      {s.summary_guidance && (
                        <p className="text-xs text-kfaint leading-relaxed border-l-2 border-krule pl-3">{s.summary_guidance}</p>
                      )}

                      <div className="flex flex-wrap gap-2 items-center pt-1">
                        <span className="text-[11px] text-kfaint">Copy to corpus:</span>
                        <select
                          value={copyTarget[s.skill_id] ?? ""}
                          onChange={(e) => setCopyTarget((prev) => ({ ...prev, [s.skill_id]: e.target.value }))}
                          className="text-xs border border-krule rounded-lg px-2 py-1.5 bg-white"
                        >
                          <option value="">Corpus…</option>
                          {writableCorpora
                            .filter((c) => c.corpusId !== s.owner_corpus_id)
                            .map((c) => (
                              <option key={c.corpusId} value={c.corpusId}>{corpusLabel(c)}</option>
                            ))}
                        </select>
                        <button
                          onClick={() => handleCopy(s)}
                          disabled={copying === s.skill_id || !copyTarget[s.skill_id]}
                          className="text-xs font-medium rounded-lg px-4 py-1.5 border border-krule text-ink hover:bg-paper transition-colors disabled:opacity-50"
                        >
                          {copying === s.skill_id ? "Copying…" : "Copy (independent)"}
                        </button>
                      </div>

                      {s.status === "draft" && (
                        <div className="flex flex-wrap gap-2 items-center pt-1">
                          <select
                            value={approveTarget[s.skill_id]?.corpusId ?? ""}
                            onChange={(e) =>
                              setApproveTarget((prev) => ({
                                ...prev,
                                [s.skill_id]: { corpusId: e.target.value, typeName: prev[s.skill_id]?.typeName ?? s.name },
                              }))
                            }
                            className="text-xs border border-krule rounded-lg px-2 py-1.5 bg-white"
                          >
                            <option value="">Corpus…</option>
                            {writableCorpora.map((c) => (
                              <option key={c.corpusId} value={c.corpusId}>{corpusLabel(c)}</option>
                            ))}
                          </select>
                          <input
                            value={approveTarget[s.skill_id]?.typeName ?? s.name}
                            onChange={(e) =>
                              setApproveTarget((prev) => ({
                                ...prev,
                                [s.skill_id]: { corpusId: prev[s.skill_id]?.corpusId ?? "", typeName: e.target.value },
                              }))
                            }
                            className="text-xs border border-krule rounded-lg px-2 py-1.5 w-44 font-mono"
                            placeholder="Type name"
                          />
                          <button
                            onClick={() => handleApprove(s)}
                            disabled={approving === s.skill_id || !approveTarget[s.skill_id]?.corpusId}
                            className="text-xs font-medium rounded-lg px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50"
                          >
                            {approving === s.skill_id ? "Approving…" : "Approve & link"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {skills.length === 0 && <p className="text-sm text-kslate">No skills yet.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkillsPage;
